import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';

import { Coordinate } from 'app/models/coordinate.interface';
import {
  API_URL,
  API_URL_V4,
  environment,
} from 'src/environments/environment.local';
import * as filterHelpers from '../helpers/filter.helpers';
import * as formatDataHelpers from '../helpers/format-browse-data.helpers';
import * as sortingHelpers from '../helpers/sorting.helpers';
import { BrowseFilter } from '../models/filter';
import { BrowseSorting } from '../models/sorting';
import { Sport } from '../models/sport';
import { StravaSection } from 'app/modules/dashboard/models/stravaSection.model';
import {
  ApiHttpResponse,
  TrainingSection,
  VideoSection,
} from '@kinomap-dev/ng-ui-lib';

@Injectable({ providedIn: 'root' })
export class VideoApi {
  private readonly videosV4Url: string = `${API_URL_V4}/scenic`;
  private readonly limit: number = 15;
  private lastSelectedSport: Sport | null = null;
  private lastFilters: BrowseFilter[] = [];
  private lastSorting: BrowseSorting | null = null;
  private params: HttpParams = new HttpParams();
  isLastPageLoaded: boolean = false;

  constructor(private http: HttpClient) {
    this.params = this.params.set('limit', this.limit).set('page', 1);
  }

  getVideos$(
    selectedSport: Sport = this.lastSelectedSport,
    filters: BrowseFilter[] = this.lastFilters,
    sorting: BrowseSorting = this.lastSorting,
    nextPage: boolean
  ): Observable<VideoSection[]> {
    this.resetParams(nextPage);
    this.params = this.params
      .set('sport', selectedSport.name)
      .set('sortDirection', filterHelpers.getSortDirectionParam(filters))
      .set('sortType', sortingHelpers.getSortTypeParam(sorting));
    this.setFilterParams(filters);

    if (!nextPage) {
      this.params = this.params.set('page', 1);
      this.isLastPageLoaded = false;
    }

    return this.http
      .get<
        ApiHttpResponse<VideoSection[]>
      >(this.videosV4Url, { params: this.params })
      .pipe(
        map((res: ApiHttpResponse<VideoSection[]>) => {
          const currentPage: number = +res.meta.current_page;
          const lastPage: number = +res.meta.last_page;

          if (currentPage === lastPage) {
            this.isLastPageLoaded = true;
          } else {
            this.params = this.params.set('page', currentPage + 1);
          }

          this.lastSelectedSport = { ...selectedSport };
          this.lastFilters = [
            ...filters.map((f) => {
              return { ...f };
            }),
          ];
          this.lastSorting = { ...sorting };

          return res.data;
        })
      );
  }

  private resetParams(nextPage: boolean): void {
    const currentPage: string = this.params.get('page') ?? '1';
    const pageToUse: string = nextPage ? currentPage : '1';
    this.params = new HttpParams()
      .set('limit', this.limit)
      .set('page', pageToUse);
  }

  private setFilterParams(filters: BrowseFilter[]): void {
    this.setDurationParams(filters);
    this.setDistanceParams(filters);
    this.setSlopeParams(filters);
    this.setDifficultiesParam(filters);
    this.setQualitiesParam(filters);
    this.setProgressParams(filters);
    this.setSoundParams(filters);
    this.setSpeedParams(filters);
    this.setPowerParams(filters);
    this.setTagParam(filters);
    this.setInAppParam(filters);
    this.setClimbCategoriesParam(filters);
    this.setRegionsParams(filters);
    // this.setVehiclesParam(filters);
  }

  private setDurationParams(filters: BrowseFilter[]): void {
    const [min, max]: string[] = filterHelpers.getDurationParams(filters);
    this.params = this.params.set('minDuration', min).set('maxDuration', max);
  }

  private setDistanceParams(filters: BrowseFilter[]): void {
    const [min, max]: string[] = filterHelpers.getDistanceParams(filters);
    this.params = this.params.set('minDistance', min).set('maxDistance', max);
  }

  private setSlopeParams(filters: BrowseFilter[]): void {
    const [min, max]: string[] = filterHelpers.getSlopeParams(filters);
    this.params = this.params
      .set('minPositiveSlope', min)
      .set('maxPositiveSlope', max);
  }

  private setDifficultiesParam(filters: BrowseFilter[]): void {
    const diffParam: string[] = filterHelpers.getDifficultiesParam(filters);
    if (this.params.has('difficulties[]')) {
      this.params = this.params.delete('difficulties[]');
    }
    diffParam.forEach((difficulty: string) => {
      this.params = this.params.append('difficulties[]', difficulty);
    });
  }

  private setQualitiesParam(filters: BrowseFilter[]): void {
    const qualitiesParams: string[] = filterHelpers.getQualitiesParam(filters);
    if (this.params.has('qualities[]')) {
      this.params = this.params.delete('qualities[]');
    }
    qualitiesParams.forEach((quality: string) => {
      this.params = this.params.append('qualities[]', quality);
    });
  }

  private setProgressParams(filters: BrowseFilter[]): void {
    const { excludeComplete, excludeStarted } =
      filterHelpers.getProgressParam(filters);
    this.params = this.params
      .set('excludeComplete', excludeComplete)
      .set('excludeStarted', excludeStarted);
  }

  private setSoundParams(filters: BrowseFilter[]): void {
    const soundParam: string = filterHelpers.getSoundParam(filters);
    this.params = this.params.set('soundAvailable', soundParam);
  }

  private setSpeedParams(filters: BrowseFilter[]): void {
    const [min, max]: string[] = filterHelpers.getSpeedParams(filters);
    this.params = this.params.set('minAvgSpeed', min).set('maxAvgSpeed', max);
  }

  private setPowerParams(filters: BrowseFilter[]): void {
    const [min, max]: string[] = filterHelpers.getPowerParams(filters);
    this.params = this.params.set('minAvgPower', min).set('maxAvgPower', max);
  }

  private setRegionsParams(filters: BrowseFilter[]): void {
    const regionParam: string[] = filterHelpers.getRegionParam(filters);
    if (this.params.has('codeCountry[]')) {
      this.params = this.params.delete('codeCountry[]');
    }
    regionParam.forEach((region: string) => {
      this.params = this.params.append('codeCountry[]', region);
    });
  }

  private setTagParam(filters: BrowseFilter[]): void {
    const tagParam: number[] = filterHelpers.getTagParam(filters);
    if (this.params.has('tagIds[]')) {
      this.params = this.params.delete('tagIds[]');
    }
    tagParam.forEach((tagId: number) => {
      this.params = this.params.append('tagIds[]', tagId.toString());
    });
  }

  private setInAppParam(filters: BrowseFilter[]): void {
    const inAppParam: string = filterHelpers.getInAppParam(filters);
    this.params = this.params.set('inApp', inAppParam);
  }

  private setClimbCategoriesParam(filters: BrowseFilter[]): void {
    const climbCategoriesParam: number[] =
      filterHelpers.getClimbCategoriesParam(filters);
    if (this.params.has('climbCategories[]')) {
      this.params = this.params.delete('climbCategories[]');
    }
    climbCategoriesParam.forEach((climbCategory: number) => {
      this.params = this.params.append(
        'climbCategories[]',
        climbCategory.toString()
      );
    });
  }

  // private setVehiclesParam(filters: BrowseFilter[]): void {
  //   const vehiclesParam: string[] = filterHelpers.getVehiclesParam(filters);
  //   if (this.params.has('vehicles[]')) {
  //     this.params = this.params.delete('vehicles[]');
  //   }
  //   vehiclesParam.forEach((vehicle: string) => {
  //     this.params = this.params.append('vehicles[]', vehicle);
  //   });
  // }

  // WATCH-PAGE
  getVideoInformation(videoId: Number): Observable<VideoSection> {
    return this.http
      .get<ApiHttpResponse<VideoSection>>(this.videosV4Url + '/' + videoId)
      .pipe(
        map((res: ApiHttpResponse<VideoSection>) => {
          return formatDataHelpers.convertApiMetaDataToVideo(res);
        })
      );
  }

  getVideoInformationV4(videoId: Number): Observable<VideoSection> {
    return this.http
      .get<ApiHttpResponse<VideoSection>>(this.videosV4Url + '/' + videoId)
      .pipe(
        map((res: ApiHttpResponse<VideoSection>) => {
          return res.data;
        })
      );
  }

  getVideoLike(videoId: number, page: number): Observable<any> {
    const param: HttpParams = new HttpParams().set('page', page);
    return this.http
      .get(this.videosV4Url + '/' + videoId + '/likes', { params: param })
      .pipe(map((res: any) => res));
  }

  addVideoLike(videoId: number): Observable<any> {
    return this.http
      .post(this.videosV4Url + '/' + videoId + '/likes', {})
      .pipe(map((res: any) => res));
  }

  deleteVideoLike(videoId: number): Observable<any> {
    return this.http
      .delete(this.videosV4Url + '/' + videoId + '/likes', {})
      .pipe(map((res: any) => res));
  }

  getComments(videoId: number): Observable<any> {
    return this.http
      .get(this.videosV4Url + '/' + videoId + '/comments', {})
      .pipe(map((res: any) => res.data));
  }

  getChildsSections(videoId: number, page: number): Observable<any> {
    return this.http.get(
      this.videosV4Url + '/' + videoId + '/childs-sections',
      {}
    );
  }

  getVideoGPX(videoId: number) {
    const url: string = environment.api.watch.getVideoGPX.replace(
      '{videoId}',
      videoId + ''
    );
    const httpOptions = { responseType: 'blob' as 'json' };
    return this.http.get(url, httpOptions);
  }

  getStravaSections(videoId: number): Observable<StravaSection[]> {
    const url: string = environment.api.watch.getStravaSection.replace(
      '{videoId}',
      videoId + ''
    );
    return this.http.get(url).pipe(
      map((res: any) => {
        return Array.isArray(res.data) ? res.data : [res.data];
      })
    );
  }

  getEditScenicVideo(videoId: number): Observable<any> {
    const url: string = environment.api.watch.edit.replace(
      '{videoId}',
      videoId.toString()
    );
    return this.http.get(url);
  }

  getEditCoachingVideo(coachingId: number): Observable<any> {
    const url: string = environment.api.coaching.edit.replace(
      '{videoId}',
      coachingId.toString()
    );
    return this.http.get(url);
  }

  postEditScenicVideo(videoId: number, data: any): Observable<any> {
    const url: string = environment.api.watch.edit.replace(
      '{videoId}',
      videoId.toString()
    );
    return this.http.post(url, data);
  }

  postEditCoachingVideo(videoId: number, data: any): Observable<any> {
    const url: string = environment.api.coaching.edit.replace(
      '{videoId}',
      videoId.toString()
    );
    return this.http.post(url, data);
  }

  getAllCoachingLang(): Observable<any> {
    const url: string = environment.api.coaching.lang;
    return this.http.get(url);
  }

  // VIDEO-PLAYER
  getVideoUrls(videoId: Number): Observable<any> {
    return this.http
      .get(this.videosV4Url + '/' + videoId + '/urls')
      .pipe(map((res: any) => res.data));
  }

  updateThumbnail(
    videoId: string,
    time: number,
    videoType: 'watch' | 'coaching'
  ): Observable<any> {
    switch (videoType) {
      case 'watch':
        return this.http
          .patch(
            environment.api.watch.updateThumbnail.replace(
              '{hashId}',
              videoId.toString()
            ),
            { time }
          )
          .pipe(map((res) => res));

      case 'coaching':
        return this.http
          .patch(
            environment.api.coaching.updateThumbnail.replace(
              '{hashId}',
              videoId.toString()
            ),
            { time }
          )
          .pipe(map((res) => res));

      default:
        console.error('This VIDEO have no type (watch | coaching)');
        break;
    }
  }

  // CHART-PLAYER
  getVideoCoordinates(videoId: number | string): Observable<Coordinate[]> {
    return this.http
      .get(this.videosV4Url + '/' + videoId + '/coordinates')
      .pipe(
        map((res: any) => {
          return res.data.map((item) => {
            return formatDataHelpers.convertApiDataToCorrdinate(item);
          });
        })
      );
  }

  getTrainingSections(videoId: number | string): Observable<TrainingSection[]> {
    return this.http.get(`${API_URL}/videos/${videoId}/training-sections`).pipe(
      map((res: any) => {
        return res.data.map((item) => {
          return item;
        });
      })
    );
  }

  // MAP-PLAYER
  editVideoSync(videoId: number, startTime: number): Observable<any> {
    return this.http
      .post(
        this.videosV4Url +
          '/' +
          videoId +
          '/editSync' +
          '?startTime=' +
          startTime,
        {}
      )
      .pipe(
        map((res: any) => {
          return res.data;
        })
      );
  }

  // HOME
  getHomeVideos$(selectedSport: Sport): Observable<VideoSection[]> {
    const params: HttpParams = new HttpParams().set(
      'sport',
      selectedSport.name
    );

    return this.http
      .get<ApiHttpResponse<VideoSection[]>>(this.videosV4Url, {
        params,
      })
      .pipe(
        map(
          (videoSection: ApiHttpResponse<VideoSection[]>) => videoSection.data
        )
      );
  }

  getVideosOfTheWeek$(selectedSport: Sport): Observable<VideoSection[]> {
    const params: HttpParams = new HttpParams().set(
      'sport',
      selectedSport.name
    );
    return this.http
      .get<
        ApiHttpResponse<VideoSection[]>
      >(`${this.videosV4Url}/weekly`, { params })
      .pipe(
        map((response: ApiHttpResponse<VideoSection[]>) =>
          response.data.filter(
            (videoSection) => videoSection.status !== 'deleted'
          )
        )
      );
  }

  getRecommendedVideos$(selectedSport: Sport): Observable<VideoSection[]> {
    const params: HttpParams = new HttpParams()
      .set('sport', selectedSport.name)
      .set('isVipManufacturer', true);
    return this.http
      .get<ApiHttpResponse<VideoSection[]>>(
        `${API_URL_V4}/scenic/recommended`,
        {
          params,
        }
      )
      .pipe(
        map(
          (videoSections: ApiHttpResponse<VideoSection[]>) => videoSections.data
        )
      );
  }

  getTop10Videos$(selectedSport: Sport): Observable<VideoSection[]> {
    const params: HttpParams = new HttpParams().set(
      'sport',
      selectedSport.name
    );
    return this.http
      .get<ApiHttpResponse<VideoSection[]>>(`${this.videosV4Url}/top10`, {
        params,
      })
      .pipe(
        map(
          (videoSections: ApiHttpResponse<VideoSection[]>) => videoSections.data
        )
      );
  }

  // FAVORITE 📋
  addToFavorite(videoSectionId: string | number): Observable<any> {
    const favoriteUrl: string = `${environment.api.dashboard.myFavorites}/${videoSectionId}`;
    return this.http.post(favoriteUrl, {});
  }

  removeFromFavorite(videoSectionId: string | number): Observable<any> {
    const favoriteUrl: string = `${environment.api.dashboard.myFavorites}/${videoSectionId}`;
    return this.http.delete(favoriteUrl, {});
  }
}
